/** @format */

import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useCallback, useEffect, useRef, useState } from "react";
import { useHotkeys } from "react-hotkeys-hook";
import { useUnreadNotificationCount } from "@/api/notificationsApi";
import { useCTAControl } from "@/hooks/useCTAControl";
import { usePushNotifications } from "@/hooks/usePushNotifications";
import InlineErrorBoundary from "@/utils/InlineErrorBoundary";
import { faBell } from "@/utils/iconLibraryTreeShake";
import { safeStorage } from "@/utils/storage";
import { getVisitCount } from "@/utils/visitCounter";
import { NotificationDropdown } from "./NotificationDropdown";

const DISMISS_KEY = "push-prompt-dismissed";
const DISMISS_DAYS = 7;
const MIN_VISITS_BEFORE_PROMPT = 3;

function wasDismissedRecently(): boolean {
  const raw = safeStorage.getItem(DISMISS_KEY);
  if (!raw) {
    return false;
  }
  const ts = Number(raw);
  if (Number.isNaN(ts)) {
    return false;
  }
  return Date.now() - ts < DISMISS_DAYS * 24 * 60 * 60 * 1000;
}

export const NotificationBell = ({ onPushEnabled }: { onPushEnabled?: () => void }) => {
  const { data: unreadCount = 0 } = useUnreadNotificationCount();
  const { isSubscribed, isSupported, isEnabled, permission, refreshPermissionStatus } =
    usePushNotifications();

  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const didAutoOpen = useRef(false);

  const shouldPrompt =
    isSupported && isEnabled && !isSubscribed && permission !== "denied";

  const ctaEligible =
    shouldPrompt &&
    !wasDismissedRecently() &&
    getVisitCount() >= MIN_VISITS_BEFORE_PROMPT;
  const { isActive: ctaActive, dismiss: dismissCTA } = useCTAControl("notifications", {
    experimentKey: "cta-notification-prompt",
    isEligible: ctaEligible,
  });

  const recordDismissal = useCallback(() => {
    safeStorage.setItem(DISMISS_KEY, String(Date.now()));
    dismissCTA();
  }, [dismissCTA]);

  // Auto-open when this CTA wins the round (cooldown + priority handled in ctaStore).
  useEffect(() => {
    if (!ctaActive || didAutoOpen.current) {
      return;
    }
    didAutoOpen.current = true;
    const id = setTimeout(() => setIsOpen(true), 1500);
    return () => clearTimeout(id);
  }, [ctaActive]);

  // Close on outside click
  useEffect(() => {
    if (!isOpen) {
      return;
    }
    function handleClick(e: MouseEvent) {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setIsOpen(false);
        if (shouldPrompt && didAutoOpen.current) {
          recordDismissal();
        }
      }
    }
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, [isOpen, shouldPrompt, recordDismissal]);

  // Close on Escape (works from anywhere, including focused form fields).
  useHotkeys(
    "escape",
    () => {
      setIsOpen(false);
      if (shouldPrompt && didAutoOpen.current) {
        recordDismissal();
      }
    },
    { enabled: isOpen, enableOnFormTags: true },
    [shouldPrompt, recordDismissal]
  );

  const toggle = useCallback(() => {
    refreshPermissionStatus();
    setIsOpen(prev => {
      const next = !prev;
      // If closing and we auto-opened with prompt showing, record dismissal
      if (!next && shouldPrompt && didAutoOpen.current) {
        recordDismissal();
      }
      return next;
    });
  }, [refreshPermissionStatus, shouldPrompt, recordDismissal]);

  const handlePushEnabled = useCallback(() => {
    setIsOpen(false);
    onPushEnabled?.();
  }, [onPushEnabled]);

  return (
    <div className="relative" ref={containerRef}>
      <button
        onClick={toggle}
        className="relative text-left border flex items-center rounded-lg border-slate-500 py-1.5 px-3 hover:bg-slate-800 transition-colors"
        aria-label={`Notifications${unreadCount > 0 ? ` (${unreadCount} unread)` : ""}`}
        aria-expanded={isOpen}
        type="button"
      >
        <FontAwesomeIcon icon={faBell} className="text-slate-200" />

        {/* Unread count badge (top-right, red) */}
        {unreadCount > 0 && (
          <span className="absolute -top-1 -right-1 bg-red-600 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center animate-pulse">
            {unreadCount > 99 ? "99+" : unreadCount}
          </span>
        )}

        {/* Push not enabled dot (bottom-left, orange) */}
        {shouldPrompt && (
          <span className="absolute -bottom-0.5 -left-0.5 h-2.5 w-2.5 rounded-full bg-orange-500 ring-2 ring-slate-900" />
        )}
      </button>

      {/* Dropdown panel */}
      <div
        className={`fixed sm:absolute z-30 sm:right-0 left-2 right-2 sm:left-auto mt-2 sm:w-96 origin-top-right transition ease-out duration-200 ${
          isOpen
            ? "opacity-100 translate-y-0"
            : "opacity-0 translate-y-1 pointer-events-none"
        }`}
      >
        {isOpen && (
          <InlineErrorBoundary name="Notifications">
            <NotificationDropdown onPushEnabled={handlePushEnabled} />
          </InlineErrorBoundary>
        )}
      </div>
    </div>
  );
};
